<template>
	<div class="all-templates-dropdown" ref="dropdownRef" :class="mergedSettings.class" :style="mergedSettings.style">
		<nk-button class="all-templates-trigger" :class="mergedSettings.triggerClass" :options="triggerButtonOptions" @click="toggleDropdown"></nk-button>

		<div v-if="isOpen" class="all-templates-popover" :class="mergedSettings.popoverClass" @click.stop>
			<div class="popover-search">
				<icon name="magnifying-glass" group="regular" class="search-icon" />
				<nk-input v-model="searchQuery" :options="searchInputOptions" class="search-input"></nk-input>
			</div>

			<div class="popover-list">
				<nk-button v-for="item in filteredItems" :key="getItemKey(item)" class="popover-template-item" :class="{ active: isItemActive(item) }" :options="getItemButtonOptions(item)" @click="selectItem(item)"></nk-button>
			</div>

			<nk-button v-if="mergedSettings.showNewButton" class="popover-new-btn" :options="newButtonOptions" @click="onNewClicked"></nk-button>
		</div>
	</div>
</template>

<script lang="ts">
	import { defineComponent, PropType } from 'vue'

	export default defineComponent({
		name: 'template-dropdown',
		props: {
			modelValue: {
				type: [Object, String, Number] as PropType<any>,
				default: null
			},
			data: {
				type: Array as PropType<any[]>,
				default: () => []
			},
			options: {
				type: Object as PropType<Record<string, any>>,
				default: () => ({})
			}
		},
		emits: ['update:modelValue', 'change', 'new'],
		data() {
			return {
				isOpen: false,
				searchQuery: ''
			}
		},
		computed: {
			defaultSettings(): Record<string, any> {
				return {
					title: this.$t('salary.advance-filter.all-templates'),
					searchPlaceholder: this.$t('salary.advance-filter.search-templates'),
					newButtonTitle: this.$t('salary.advance-filter.new'),
					showNewButton: true,
					valueField: 'id',
					displayField: 'name',
					disabled: false,
					triggerClass: '',
					popoverClass: '',
					class: '',
					style: ''
				}
			},
			mergedSettings(): Record<string, any> {
				return Object.assign({}, this.defaultSettings, this.options || {})
			},
			triggerButtonOptions(): any {
				return {
					title: this.mergedSettings.title || this.$t('salary.advance-filter.all-templates'),
					icon: this.isOpen ? 'fa-regular fa-chevron-up' : 'fa-regular fa-chevron-down',
					isPrimary: false,
					isOutline: false,
					disabled: !!this.mergedSettings.disabled
				}
			},
			searchInputOptions(): any {
				return {
					placeholder: this.mergedSettings.searchPlaceholder || this.$t('salary.advance-filter.search-templates'),
					mode: 'string'
				}
			},
			newButtonOptions(): any {
				return {
					isPrimary: true,
					hideIcon: true,
					title: this.mergedSettings.newButtonTitle || this.$t('salary.advance-filter.new')
				}
			},
			filteredItems(): any[] {
				const query = (this.searchQuery || '').trim().toLowerCase()
				const items = this.data || []
				if (!query)
					return items
				const displayField = this.mergedSettings.displayField || 'name'
				return items.filter(item => {
					const val = item ? (item[displayField] || item.name || item.title || '') : ''
					return String(val).toLowerCase().includes(query)
				})
			}
		},
		mounted() {
			document.addEventListener('click', this.handleOutsideClick)
		},
		beforeUnmount() {
			document.removeEventListener('click', this.handleOutsideClick)
		},
		methods: {
			toggleDropdown() {
				if (this.mergedSettings.disabled)
					return
				this.isOpen = !this.isOpen
				if (this.isOpen) {
					this.searchQuery = ''
				}
			},
			handleOutsideClick(event: MouseEvent) {
				if (!this.isOpen)
					return
				const el = this.$refs.dropdownRef as HTMLElement | undefined
				if (el && !el.contains(event.target as Node)) {
					this.isOpen = false
				}
			},
			getItemKey(item: any): string | number {
				const valueField = this.mergedSettings.valueField || 'id'
				return item ? (item[valueField] ?? item.id ?? item.name) : Math.random()
			},
			isItemActive(item: any): boolean {
				if (!this.modelValue || !item)
					return false
				const valueField = this.mergedSettings.valueField || 'id'
				const currentVal = typeof this.modelValue === 'object' ? this.modelValue[valueField] : this.modelValue
				const itemVal = typeof item === 'object' ? item[valueField] : item
				return currentVal !== undefined && itemVal !== undefined && currentVal === itemVal
			},
			getItemButtonOptions(item: any): any {
				const displayField = this.mergedSettings.displayField || 'name'
				return {
					title: item ? (item[displayField] || item.name || item.title || '') : '',
					hideIcon: true,
					isPrimary: false,
					isOutline: false
				}
			},
			selectItem(item: any) {
				this.isOpen = false
				this.$emit('update:modelValue', item)
				this.$emit('change', item)
			},
			onNewClicked() {
				this.isOpen = false
				this.$emit('new')
				if (typeof this.mergedSettings.onNew === 'function') {
					this.mergedSettings.onNew()
				}
			}
		}
	})
</script>

<style lang="less" scoped>
	@import (reference) '@/styles/site.config.less';

	.all-templates-dropdown {
		position: relative;
		display: inline-block;

		.all-templates-trigger {
			display: inline-flex;
			align-items: center;
			justify-content: space-between;
			flex-direction: row-reverse;
			background-color: #ffffff;
			border: 1px solid #CAD5E2;
			border-radius: 8px;
			padding: 5px 12px;
			height: 34px;
			box-shadow: none;
			gap: 8px;
			transition: all 0.2s ease-in-out;

			:deep(.icon) {
				font-size: 11px;
				color: #64748B;
				margin: 0;
			}

			:deep(.button-title) {
				font-size: 13px;
				color: #334155;
			}

			&:hover {
				background-color: #F8FAFC;
				border-color: #94A3B8;
			}
		}

		.all-templates-popover {
			position: absolute;
			top: calc(100% + 8px);
			left: 0;
			width: 220px;
			background-color: #ffffff;
			border-radius: 12px;
			box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 8px 10px -6px rgba(0, 0, 0, 0.05);
			border: 1px solid #E2E8F0;
			padding: 12px;
			z-index: 1000;

			.popover-search {
				position: relative;
				margin-bottom: 10px;

				.search-icon {
					position: absolute;
					right: 10px;
					top: 50%;
					transform: translateY(-50%);
					color: #94A3B8;
					font-size: 12px;
					z-index: 2;
					pointer-events: none;
				}

				.search-input {
					:deep(input) {
						border-radius: 8px;
						border: 1px solid #E2E8F0;
						padding-right: 28px;
						font-size: 12px;
						height: 32px;
					}
				}
			}

			.popover-list {
				display: flex;
				flex-direction: column;
				gap: 6px;
				max-height: 180px;
				overflow-y: auto;
				margin-bottom: 12px;
				padding-left: 2px;
				scrollbar-width: thin;

				.popover-template-item {
					width: 100%;
					display: flex;
					align-items: center;
					justify-content: center;
					background-color: #ffffff;
					border: 1px solid #E2E8F0;
					border-radius: 8px;
					padding: 4px 8px;
					height: 32px;
					box-shadow: none;
					transition: all 0.15s ease;

					:deep(.button-title) {
						font-size: 12px;
						color: #334155;
					}

					&:hover {
						background-color: #F8FAFC;
						border-color: #CBD5E1;
					}

					&.active {
						border-color: @primary;
						background-color: fade(@primary, 6%);

						:deep(.button-title) {
							color: @primary;
							font-weight: 600;
						}
					}
				}
			}

			.popover-new-btn {
				width: 100%;
				background-color: @primary;
				border-color: @primary;
				color: #ffffff;
				border-radius: 8px;
				height: 34px;
				display: flex;
				align-items: center;
				justify-content: center;

				:deep(.button-title) {
					color: #ffffff;
					font-size: 13px;
					font-weight: 500;
				}

				&:hover {
					background-color: darken(@primary, 8%);
					border-color: darken(@primary, 8%);
				}
			}
		}
	}
</style>
